import React, { useEffect, useRef } from 'react';

import type { ChatMessage } from '../types';
import { CAPABILITIES_URL, EXAMPLE_PROMPTS, PRICING_SLACK_URL } from '../constants';
import {
  Disclaimer,
  Dot,
  EmptyState,
  ExamplePrompt,
  ExamplePromptList,
  MessageList,
  PanelBody,
  StreamingDots,
  WelcomeCallout,
  WelcomeFooter,
  WelcomeIntro,
  WelcomeLabel,
  WelcomeState,
} from '../styles';

import { MessageBubble } from './MessageBubble';
import { ChatInput } from './ChatInput';

// How close to the bottom still counts as "following the conversation".
const BOTTOM_THRESHOLD_PX = 40;

interface Props {
  messages: ChatMessage[];
  onSend: (message: string) => void;
  onResolveAction: (decision: 'yes' | 'no') => void | Promise<boolean>;
  isStreaming: boolean;
  onComposerFocus?: () => void;
  // Null until the first turn creates a session; feedback is gated on it downstream.
  sessionId?: string | null;
  // True while the resume lookup is in flight, before we know whether there is a
  // conversation to restore.
  isHydrating?: boolean;
  // Leading messages restored from a previous visit. See `lastProposalIndex`.
  hydratedCount?: number;
}

export function ChatPanel({
  messages,
  onSend,
  onResolveAction,
  isStreaming,
  onComposerFocus,
  sessionId,
  isHydrating = false,
  hydratedCount = 0,
}: Props) {
  const listRef = useRef<HTMLDivElement>(null);
  // Whether the transcript is scrolled to the bottom. Streaming appends a token at a
  // time, so following the tail must not fight a user who has scrolled up to read
  // back: once they leave the bottom, auto-scroll stops until they return.
  const pinnedToBottomRef = useRef(true);

  const handleScroll = () => {
    const list = listRef.current;
    if (list) {
      const distanceFromBottom = list.scrollHeight - list.scrollTop - list.clientHeight;
      pinnedToBottomRef.current = distanceFromBottom < BOTTOM_THRESHOLD_PX;
    }
  };

  // Only the latest *proposal* stays clickable. Keyed on carrying actions, not on
  // being the last assistant message: approving a card appends a further assistant
  // message, which must not retire the card the user just acted on.
  //
  // Restored messages are excluded: a proposal recovered from a transcript may be
  // hours old and describe a mutation against an estimate that has changed since,
  // so reloading the page must not leave it one click from being applied. Asking
  // again re-proposes it against current data.
  const lastProposalIndex = messages.reduce(
    (acc, msg, index) =>
      index >= hydratedCount && msg.actions && msg.actions.length > 0 ? index : acc,
    -1
  );

  useEffect(() => {
    const list = listRef.current;
    if (list && pinnedToBottomRef.current) {
      // Set scrollTop rather than scrollIntoView: this scrolls only the transcript,
      // never the page behind the drawer, and instantly — a smooth animation
      // restarted on every token just fights itself.
      list.scrollTop = list.scrollHeight;
    }
  }, [messages]);

  return (
    <PanelBody>
      <MessageList data-testid="message-list" onScroll={handleScroll} ref={listRef}>
        {messages.length === 0 &&
          (isHydrating ? (
            <EmptyState>Loading your conversation…</EmptyState>
          ) : (
            <WelcomeState>
              <WelcomeIntro>
                👋 Ask me about Confluent Cloud pricing, or tell me what to change on this estimate.
              </WelcomeIntro>
              <WelcomeLabel>Try asking</WelcomeLabel>
              <ExamplePromptList>
                {EXAMPLE_PROMPTS.map((prompt) => (
                  <ExamplePrompt key={prompt}>{prompt}</ExamplePrompt>
                ))}
              </ExamplePromptList>
              <WelcomeCallout href={CAPABILITIES_URL} rel="noopener noreferrer" target="_blank">
                📖 What AskCommit (Beta) can and can&apos;t do →
              </WelcomeCallout>
              <WelcomeFooter>
                <p>🗣️ Feedback: Thumbs down and type what was wrong or could be improved.</p>
                <p>
                  💲 Pricing issues or questions? Contact{' '}
                  <a href={PRICING_SLACK_URL} rel="noopener noreferrer" target="_blank">
                    #pricing-911
                  </a>
                  .
                </p>
              </WelcomeFooter>
            </WelcomeState>
          ))}
        {messages.map((msg, i) => {
          // The user message this assistant reply answered: useChat appends the
          // user + assistant pair together, so the immediately preceding message.
          const priorUserQuery =
            i > 0 && messages[i - 1].role === 'user' ? messages[i - 1].content : '';
          return (
            <MessageBubble
              isActionable={i === lastProposalIndex}
              isStreaming={isStreaming && i === messages.length - 1}
              key={msg.id}
              message={msg}
              onResolveAction={onResolveAction}
              sessionId={sessionId ?? undefined}
              userQuery={priorUserQuery}
            />
          );
        })}
        {isStreaming && (
          <StreamingDots>
            <Dot $delay={0} />
            <Dot $delay={0.2} />
            <Dot $delay={0.4} />
          </StreamingDots>
        )}
      </MessageList>

      <ChatInput disabled={isStreaming} onFocus={onComposerFocus} onSend={onSend} />
      <Disclaimer>
        AskCommit uses AI and can make mistakes. Please double-check responses.
      </Disclaimer>
    </PanelBody>
  );
}
